import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileCategoryOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  const handleCategoryClick = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const isHome = location.pathname === '/';

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/92 backdrop-blur-md border-b border-background-200/70">
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16">
          <a
            href="/"
            className="flex items-center gap-2 text-foreground-950 font-heading text-lg md:text-xl font-bold tracking-tight whitespace-nowrap"
          >
            <span className="w-7 h-7 flex items-center justify-center">
              <i className="ri-leaf-line text-primary-500 text-xl"></i>
            </span>
            <span className="hidden sm:inline">Mulebuy Spreadsheet</span>
            <span className="sm:hidden">Mulebuy</span>
          </a>

          <div className="hidden md:flex items-center gap-2">
            <a
              href="/"
              className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-colors duration-200 whitespace-nowrap ${
                isHome
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              Home
            </a>

            <a
              href="/what-is-mulebuy-spreadsheet"
              className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-colors duration-200 whitespace-nowrap ${
                location.pathname === '/what-is-mulebuy-spreadsheet'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              Shopping Guide
            </a>

            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-foreground-300/40 bg-transparent text-foreground-700 text-sm font-medium cursor-pointer hover:border-foreground-500/60 transition-colors duration-200 whitespace-nowrap"
              >
                <span>Categories</span>
                <span className="w-4 h-4 flex items-center justify-center">
                  {dropdownOpen ? (
                    <i className="ri-arrow-up-s-line text-xs transition-transform duration-200"></i>
                  ) : (
                    <i className="ri-arrow-down-s-line text-xs transition-transform duration-200"></i>
                  )}
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 border border-background-200/80 rounded-2xl shadow-lg py-3 z-50 max-h-96 overflow-y-auto">
                  <div className="px-4 pb-2 mb-2 border-b border-background-100">
                    <span className="text-xs font-semibold text-foreground-400 tracking-wider uppercase">Shop by Category</span>
                  </div>
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      type="button"
                      onClick={() => handleCategoryClick(cat.url)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors duration-150 whitespace-nowrap group"
                    >
                      <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      <span className="flex-1">{cat.name}</span>
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        <i className="ri-external-link-line text-xs text-foreground-400"></i>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              Mulebuy Spreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold cursor-pointer hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold cursor-pointer whitespace-nowrap"
            >
              Mulebuy
            </a>
            <button
              type="button"
              onClick={() => { setMobileMenuOpen(!mobileMenuOpen); setMobileCategoryOpen(false); }}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-foreground-300/40 cursor-pointer text-foreground-700"
            >
              {mobileMenuOpen ? (
                <i className="ri-close-line text-lg"></i>
              ) : (
                <i className="ri-menu-line text-lg"></i>
              )}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 animate-in slide-in-from-top-2 duration-200">
          <div className="px-4 py-4 flex flex-col gap-1">
            <a
              href="/"
              className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer transition-colors duration-150"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100">
                <i className="ri-home-4-line text-primary-500"></i>
              </span>
              Home
            </a>
            <a
              href="/what-is-mulebuy-spreadsheet"
              className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer transition-colors duration-150"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100">
                <i className="ri-book-open-line text-accent-500"></i>
              </span>
              Shopping Guide
            </a>

            <button
              type="button"
              onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
              className="flex items-center justify-between px-3 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer transition-colors duration-150 w-full"
            >
              <span className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-grid-line text-secondary-500"></i>
                </span>
                Categories
              </span>
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`text-xs transition-transform duration-200 ${mobileCategoryOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
              </span>
            </button>

            {mobileCategoryOpen && (
              <div className="ml-3 pl-2 border-l-2 border-background-200/70 flex flex-col gap-0.5 animate-in slide-in-from-top-1 duration-150">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    type="button"
                    onClick={() => handleCategoryClick(cat.url)}
                    className="flex items-center gap-3 px-3 py-2.5 text-left text-sm text-foreground-600 hover:bg-background-100 rounded-lg cursor-pointer transition-colors duration-150"
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="block truncate">{cat.name}</span>
                      <span className="text-xs text-foreground-400">{cat.itemCount}+ items</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div className="border-t border-background-200/70 my-2 pt-2 flex flex-col gap-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="mx-3 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer text-center whitespace-nowrap"
              >
                Mulebuy Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="mx-3 px-4 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold cursor-pointer text-center whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}